import { ensureAnonSession, supabase } from '../lib/supabase'
import { EMPTY_DOC, parseDoc, type TranscriptDoc } from './transcriptDoc'
import { readCues, type Cue } from './transcriptCues'
import type { TranscriptRevision } from '../types/portal'

/**
 * Stored transcripts.
 *
 * `transcript_versions` in Supabase is the ONLY source of truth. A transcript
 * is shared work: Portal uploads it, and anyone holding the project link may
 * correct it. Every save is an INSERT — nothing is ever updated or deleted, so
 * no state can be destroyed, which matches the append-only RLS in place
 * (SELECT and INSERT under has_project_access, no UPDATE, no DELETE).
 *
 * Ids are resolved by direct query every time, never from a cache. A cached
 * deliverable map goes stale the moment a deliverable is added, and a stale
 * map silently produced inserts that never happened.
 */

/** Who Portal is recorded as for the original upload. */
export const PORTAL_EDITOR = 'Portal'

/** Recorded against the deliverable so Portal knows which document this came from. */
export const PORTAL_UPLOAD_EDITOR = 'Portal upload'

export interface StoredTranscript {
  /** The latest saved text, struck wording excluded. */
  text: string
  /** The latest saved document, including its tracked deletions. */
  doc: TranscriptDoc
  /** Every saved state, oldest first — the original upload is the first. */
  revisions: TranscriptRevision[]
  /**
   * Timings, when the transcript arrived with them — an SRT from Descript,
   * or an uploaded document whose paragraphs are stamped "[00:01:24]".
   * Optional everywhere and for good: a typed transcript has none, every
   * transcript made before 28 Sep 2026 has none, and none of them is broken.
   */
  cues?: Cue[]
  /**
   * The exact text `cues` was measured against — the content of the revision
   * carrying them, which is NOT necessarily the text now on screen. Passages
   * are found in this, by their words, to get a timecode. See
   * `timingForPassage` in transcriptCues.ts for why it is done that way.
   */
  cueText?: string
}

export type TranscriptLoad =
  | { status: 'ok'; transcript: StoredTranscript | null }
  | { status: 'error'; message: string }

export interface WriteResult {
  ok: boolean
  /** The stored row id, so the interface can adopt it. */
  id?: string
  /** The revision number the database assigned. */
  versionNumber?: number
  error?: string
}

const WRITE_ERROR = 'Transcript could not be saved.'

/** The numbering function is absent, rather than refusing the call. */
const MISSING_FUNCTION = new Set(['PGRST202', '42883'])

/** Postgres "duplicate key value violates unique constraint". */
const UNIQUE_VIOLATION = '23505'

function describe(error: unknown): string {
  const detail = error as { message?: string; code?: string } | null
  if (!detail) return ''
  return `${detail.message ?? String(error)}${detail.code ? ` (${detail.code})` : ''}`
}

function warn(what: string, error: unknown) {
  if (!error) return
  console.warn(`[supabase] ${what} failed: ${describe(error)}`)
}

/** The stored revision number, or 0 where a row predates the sequence. */
function numberOf(row: Record<string, unknown>): number {
  const value = Number(row.version_number)
  return Number.isFinite(value) ? value : 0
}

function revisionOf(row: Record<string, unknown>): TranscriptRevision {
  const created = String(row.created_at ?? '')
  const at = new Date(created).getTime()
  const text = String(row.content ?? '')
  return {
    id: String(row.id ?? ''),
    text,
    /* The stored document carries the tracked deletions. A row saved before
       tracked changes existed simply opens as plain paragraphs. */
    doc: parseDoc(row.marks, text),
    editorName: String(row.editor_name ?? PORTAL_EDITOR),
    at: Number.isNaN(at) ? Date.now() : at,
  }
}

/** A row with no content is not a transcript; it must not read as an empty one. */
function usable(rows: Record<string, unknown>[]): Record<string, unknown>[] {
  return rows.filter((row) => String(row.content ?? '').trim().length > 0)
}

/**
 * The timings for a transcript: the LATEST revision that actually carries
 * cues, with the text they were measured against.
 *
 * The latest rather than the first, because a re-run of the Descript worker
 * produces a newer, better-timed version and that one should win. A reader's
 * own save carries no cues and therefore never displaces them — which is
 * exactly what keeps timecodes alive through light corrections.
 */
function cuesFrom(rows: Record<string, unknown>[]): { cues: Cue[]; cueText: string } {
  for (let index = rows.length - 1; index >= 0; index -= 1) {
    const cues = readCues(rows[index].cues)
    if (cues.length) return { cues, cueText: String(rows[index].content ?? '') }
  }
  return { cues: [], cueText: '' }
}

/** Every column a read needs. One list, so the two readers cannot drift. */
const REVISION_COLUMNS = 'id, content, marks, editor_name, created_at, version_number, cues'

/* ------------------------------------------------------------------ ids ---- */

interface Ids {
  projectId: string
  deliverableId: string
}

/**
 * The real project and deliverable ids for a slug and deliverable key.
 *
 * Resolved live on every call. Both queries run under the caller's own
 * session, so a client on a project link and signed-in staff each get exactly
 * what their RLS allows and nothing more.
 */
async function resolveIds(
  projectSlug: string,
  deliverableKey: string,
): Promise<{ ids: Ids | null; error?: string }> {
  if (!supabase || !projectSlug || !deliverableKey) return { ids: null }

  const { data: project, error: projectError } = await supabase
    .from('projects')
    .select('id')
    .eq('slug', projectSlug)
    .maybeSingle()

  if (projectError) {
    warn('transcript project lookup', projectError)
    return { ids: null, error: describe(projectError) }
  }
  if (!project?.id) return { ids: null, error: 'Project not found for this link.' }

  const { data: deliverable, error: deliverableError } = await supabase
    .from('deliverables')
    .select('id')
    .eq('project_id', project.id)
    .eq('deliverable_key', deliverableKey)
    .maybeSingle()

  if (deliverableError) {
    warn('transcript deliverable lookup', deliverableError)
    return { ids: null, error: describe(deliverableError) }
  }
  if (!deliverable?.id) return { ids: null, error: 'Deliverable not found in this project.' }

  return { ids: { projectId: String(project.id), deliverableId: String(deliverable.id) } }
}

/* ----------------------------------------------------------------- reads ---- */

/**
 * The authoritative transcript for one deliverable: every saved state ordered
 * by `created_at`, with the newest as the current text.
 *
 * This is what the client transcript page reads. It does not depend on array
 * position, React state, or how the project happened to be mapped — only on
 * stored rows and their stored order. A failure is reported as a failure and
 * never silently becomes an empty transcript.
 */
export async function fetchDeliverableTranscript(
  projectSlug: string,
  deliverableKey: string,
): Promise<TranscriptLoad> {
  if (!supabase) return { status: 'ok', transcript: null }

  const { ids, error } = await resolveIds(projectSlug, deliverableKey)
  if (error) return { status: 'error', message: error }
  if (!ids) return { status: 'ok', transcript: null }

  /* Ordered by the stored revision number, with created_at deciding any row
     that predates the sequence. Never by array position or React state. */
  const { data, error: readError } = await supabase
    .from('transcript_versions')
    .select(REVISION_COLUMNS)
    .eq('project_id', ids.projectId)
    .eq('deliverable_id', ids.deliverableId)
    .order('version_number', { ascending: true, nullsFirst: true })
    .order('created_at', { ascending: true })

  if (readError) {
    warn('transcript read', readError)
    return { status: 'error', message: describe(readError) }
  }

  const rows = usable((data ?? []) as Record<string, unknown>[])
  if (rows.length === 0) return { status: 'ok', transcript: null }

  const revisions = rows.map(revisionOf)
  const current = revisions[revisions.length - 1]
  const { cues, cueText } = cuesFrom(rows)
  return {
    status: 'ok',
    transcript: { text: current.text, doc: current.doc, revisions, cues, cueText },
  }
}

/**
 * Every stored transcript for a project, by the real project id — used while
 * the project and its deliverables are loading, so both the studio side and
 * the client link start from the same stored content.
 *
 * `keyOf` maps each database deliverable id to the key the interface uses.
 */
export async function fetchTranscriptsForProject(
  projectId: string,
  keyOf: Map<string, string>,
): Promise<Map<string, StoredTranscript>> {
  const found = new Map<string, StoredTranscript>()
  if (!supabase || !projectId) return found

  const { data, error } = await supabase
    .from('transcript_versions')
    .select(`deliverable_id, ${REVISION_COLUMNS}`)
    .eq('project_id', projectId)
    .order('version_number', { ascending: true, nullsFirst: true })
    .order('created_at', { ascending: true })

  warn('transcript fetch', error)
  if (!data?.length) return found

  /* Rows are kept per deliverable so the timings can be picked from the
     right revision below, by the same rule the single-deliverable read
     uses. Reading them twice, differently, is how the two would drift. */
  const rowsByKey = new Map<string, Record<string, unknown>[]>()

  for (const row of usable(data as Record<string, unknown>[])) {
    const key = keyOf.get(String(row.deliverable_id))
    if (!key) continue
    const existing = found.get(key) ?? { text: '', doc: EMPTY_DOC, revisions: [] }
    existing.revisions.push(revisionOf(row))
    found.set(key, existing)
    const kept = rowsByKey.get(key) ?? []
    kept.push(row)
    rowsByKey.set(key, kept)
  }

  /* Stored order decides the current text — never insertion order in React. */
  for (const [key, entry] of found) {
    const current = entry.revisions[entry.revisions.length - 1]
    entry.text = current?.text ?? ''
    entry.doc = current?.doc ?? EMPTY_DOC
    const { cues, cueText } = cuesFrom(rowsByKey.get(key) ?? [])
    entry.cues = cues
    entry.cueText = cueText
  }

  return found
}

/* ---------------------------------------------------------------- writes ---- */

/**
 * AN UPLOAD THAT ALREADY CARRIES ITS OWN TIMECODES — 29 Sep 2026.
 *
 * Most transcripts a studio is sent are stamped at every speaker turn:
 * "[00:01:24] JACOB: …". Stored as they arrive, those stamps are only
 * characters, so the client page prints them and nothing is clickable —
 * which is exactly what Bloomberg Ep3 looked like: 31,106 characters and
 * not one timing.
 *
 * `save_timecoded_transcript` reads the stamps, takes them out of the text
 * (the page prints the time in its own column, and printing it twice looks
 * like a machine wrote it) and stores real cues, through the same
 * append-only revision path as everything else. The parsing lives in the
 * database beside `parse_srt` — one implementation, deliberately; see
 * utils/transcriptCues.ts.
 *
 * Returns null, quietly, whenever it does not apply: no timecodes in the
 * document, or the function not installed. A transcript with no timings is
 * a perfectly normal transcript, and the caller then stores it plainly.
 */
async function saveTimecodedUpload(
  ids: Ids,
  text: string,
  editorName: string,
): Promise<WriteResult | null> {
  if (!supabase) return null

  const { data, error } = await supabase
    .rpc('save_timecoded_transcript', {
      p_project_id: ids.projectId,
      p_deliverable_id: ids.deliverableId,
      p_text: text,
      p_editor_name: editorName,
    })
    .select('id, version_number, cue_count')
    .maybeSingle()

  if (error || !data) return null

  const row = data as Record<string, unknown>
  return { ok: true, id: String(row.id ?? ''), versionNumber: numberOf(row) }
}

/**
 * Records one saved state.
 *
 * Append-only from the caller's point of view — a client's save always
 * results in exactly one current row, but the database may fold it into the
 * revision already sitting at the top of history rather than adding a new
 * row, when `sessionId` says this save belongs to the same editing visit as
 * that one. See `p_session_id` on `insert_transcript_revision`: it is only
 * ever allowed to touch the single latest revision for a deliverable, so
 * older history can never be rewritten this way — only the newest entry
 * can be quietly refreshed in place. Portal's upload and a restore never
 * pass a `sessionId`, so they always create a fresh revision.
 *
 * Returns whether the database accepted it, so nothing in the interface can
 * report a save that did not happen.
 *
 * `user_id` is whoever is signed in: the Portal staff member on the studio
 * side, the anonymous project session on the client side.
 *
 * Note that a save made here carries no cues. That is deliberate: the words
 * have just changed, so timings measured against the previous wording are no
 * longer true of this text. The reader still sees timecodes, because they are
 * read from the last revision that HAS cues and matched by words rather than
 * by position — see `cuesFrom` above and `timingForPassage` in
 * transcriptCues.ts.
 *
 * The one exception is Portal's own upload, which is not an edit of anything
 * — it is a new document arriving, and if it is timecoded those timings are
 * true of the very text being stored. See `saveTimecodedUpload` above.
 */
export async function insertTranscriptRevision(input: {
  projectSlug: string
  deliverableKey: string
  text: string
  editorName: string
  /** The document with its tracked deletions. Omitted for a plain upload. */
  doc?: TranscriptDoc
  /**
   * Identifies one continuous editing visit (typically one page load).
   * Consecutive saves in the same visit, by the same editor, collapse into
   * the deliverable's single latest revision instead of stacking a new
   * Version History entry per Save click. Omit for an upload or a restore,
   * which should always create a new revision.
   */
  sessionId?: string
}): Promise<WriteResult> {
  if (!supabase) return { ok: false, error: 'No database connection.' }

  if (!input.text.trim()) return { ok: false, error: 'The transcript is empty.' }

  const { ids, error } = await resolveIds(input.projectSlug, input.deliverableKey)
  if (!ids) return { ok: false, error: error ?? WRITE_ERROR }

  const userId = await ensureAnonSession()

  /* A fresh document from Portal, with no tracked changes and no editing
     session behind it, is the only case where the incoming text's own
     timecodes describe the text being stored. */
  if (input.editorName === PORTAL_UPLOAD_EDITOR && !input.doc && !input.sessionId) {
    const timed = await saveTimecodedUpload(ids, input.text, input.editorName)
    if (timed) return timed
  }

  /**
   * The revision number is generated by the database, which serialises
   * concurrent saves for the same deliverable and holds a unique index on
   * (deliverable_id, version_number). Two reviewers saving seconds apart
   * therefore cannot land on the same number.
   */
  const { data, error: rpcError } = await supabase
    .rpc('insert_transcript_revision', {
      p_project_id: ids.projectId,
      p_deliverable_id: ids.deliverableId,
      p_content: input.text,
      p_editor_name: input.editorName,
      p_marks: input.doc ?? null,
      p_session_id: input.sessionId ?? null,
    })
    .select('id, version_number')
    .maybeSingle()

  if (!rpcError) {
    return {
      ok: true,
      id: (data?.id as string) ?? undefined,
      versionNumber: data ? numberOf(data as Record<string, unknown>) : undefined,
    }
  }

  /**
   * Only a genuinely absent function falls back. Anything else — a refusal,
   * an empty transcript, a missing project — is reported as it stands rather
   * than retried against a path with weaker guarantees.
   */
  if (!MISSING_FUNCTION.has((rpcError as { code?: string }).code ?? '')) {
    warn('transcript insert', rpcError)
    return { ok: false, error: `${WRITE_ERROR} ${describe(rpcError)}` }
  }

  console.warn(
    '[supabase] insert_transcript_revision is not installed — run supabase/migrations/transcript_revision_numbering.sql. Falling back to a numbered insert.',
  )

  return insertNumberedRevision({
    projectId: ids.projectId,
    deliverableId: ids.deliverableId,
    userId,
    text: input.text,
    editorName: input.editorName,
    doc: input.doc,
  })
}

/**
 * The fallback used only while the numbering function is not installed.
 *
 * It reads the highest stored number and inserts the next one, and retries on
 * a unique-index collision so a simultaneous save cannot reuse a number.
 * Still append-only, and still supplies version_number on every insert.
 *
 * This path predates the "one sitting" collapse and does not implement it —
 * it always inserts a new revision, same-session or not. It only runs when
 * `insert_transcript_revision` itself is missing from the database, which
 * the console warning above already flags as something to fix.
 */
async function insertNumberedRevision(input: {
  projectId: string
  deliverableId: string
  userId: string | null
  text: string
  editorName: string
  doc?: TranscriptDoc
}): Promise<WriteResult> {
  if (!supabase) return { ok: false, error: 'No database connection.' }

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const { data: highest, error: readError } = await supabase
      .from('transcript_versions')
      .select('version_number')
      .eq('deliverable_id', input.deliverableId)
      .order('version_number', { ascending: false, nullsFirst: false })
      .limit(1)
      .maybeSingle()

    if (readError) {
      warn('transcript revision number', readError)
      return { ok: false, error: `${WRITE_ERROR} ${describe(readError)}` }
    }

    const next = (highest ? numberOf(highest as Record<string, unknown>) : 0) + 1

    const { data, error: insertError } = await supabase
      .from('transcript_versions')
      .insert({
        project_id: input.projectId,
        deliverable_id: input.deliverableId,
        version_number: next,
        user_id: input.userId,
        content: input.text,
        marks: input.doc ?? null,
        editor_name: input.editorName,
      })
      .select('id, version_number')
      .single()

    if (!insertError) {
      return {
        ok: true,
        id: (data?.id as string) ?? undefined,
        versionNumber: data ? numberOf(data as Record<string, unknown>) : next,
      }
    }

    /* Someone else took this number in the meantime; read again and retry. */
    if ((insertError as { code?: string }).code === UNIQUE_VIOLATION) continue

    warn('transcript insert', insertError)
    return { ok: false, error: `${WRITE_ERROR} ${describe(insertError)}` }
  }

  return {
    ok: false,
    error: `${WRITE_ERROR} Another save was in progress. Try again.`,
  }
}
